import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useMemo, useEffect, useState,
} from 'react';

import { cn } from 'utils/cn';
import Button from 'components/Button';
import SwitchComponent from 'components/Switch';

import donwloadIcon from '/download.svg';

import { essentialSteps, expertSteps } from 'features/Croptimizer/utils/constants';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { BoardSteps } from 'utils/constants';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import './index.css';

export const TabBar = ({
  handlePrint,
  printLoading,
}: {
  handlePrint: () => void;
  printLoading: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { runHandler, disabled, backDisabled, showBack, onBack, showNext } = useNextHandler();
  const { activeTab, setActiveTab, step, setStep, mineralizationValues, isExpertMode, setIsExpertMode } =
    useCroptimizer();

  // Scroll detection state
  const [isTabBarVisible, setIsTabBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll detection effect
  useEffect(() => {
    if (pathname === '/croptimizer') {
      setIsTabBarVisible(true);

      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        // Always show tabbar when at the top
        setIsTabBarVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 10) {
        // Scrolling down and past 10px - hide tabbar
        setIsTabBarVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show tabbar
        setIsTabBarVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, pathname]);

  const isNotFirstStep = useMemo(() => {
    if (activeTab === 1) {
      return step !== BoardSteps.status;
    }

    return true;
  }, [step, activeTab]);

  const currentSteps = useMemo(
    () => (isExpertMode ? expertSteps : essentialSteps),
    [isExpertMode],
  );

  const currentStepIndex = useMemo(() => {
    if (activeTab === 2) {
      return currentSteps.findIndex((s) => s.id === 2 && s.boardStep === null);
    }

    return currentSteps.findIndex((s) => s.id === 1 && s.boardStep === step);
  }, [currentSteps, activeTab, step]);

  const setActiveTabHandler = (stepIndex: number) => {
    const targetStep = currentSteps[stepIndex];

    if (!targetStep) {
      return;
    }

    if (targetStep.id === 2) {
      if (
        !!mineralizationValues &&
        mineralizationValues?.annualDecrease?.length > 1
      ) {
        setActiveTab(targetStep.id);

        if (targetStep.boardStep) {
          setStep(targetStep.boardStep);
        }
      }
    } else {
      setActiveTab(targetStep.id);

      if (targetStep.boardStep) {
        setStep(targetStep.boardStep);
      }
    }
  };

  return (
    <div
      className={`tabbar-container ${
        isTabBarVisible ? 'tabbar-visible' : 'tabbar-hidden'
      }`}
    >
      <div className="mb-2 md:mb-4">
        <span
          onClick={() => navigate('/')}
          className="text-secondaryText cursor-pointer"
        >
          {t('home')} /
        </span>{' '}
        <span className="font-medium">{t('croptimizer')}</span>
      </div>
      <div
        className={
          ' flex justify-between gap-x-8 gap-y-3 md:flex-wrap items-start'
        }
      >
        <div className="flex mx-[-0.5rem] max-w-full overflow-x-scroll md:pb-2">
          {currentSteps.map((stepItem, index) => (
            <button
              onClick={() => setActiveTabHandler(index)}
              className="w-max cursor-pointer px-2"
              key={`${stepItem.id}-${stepItem.boardStep || 'strategy'}-${index}`}
            >
              <div
                className={`${
                  index === currentStepIndex ? 'font-bold' : 'text-secondaryText'
                } text-center mb-1.5 md:text-sm text-nowrap`}
              >
                {stepItem.title}
              </div>
              <div
                className={`${
                  index === currentStepIndex ? 'bg-primary' : 'bg-blue50'
                } h-[8px] rounded min-w-[83px]`}
              />
            </button>
          ))}
        </div>

        <div className="flex gap-x-8 gap-y-5 sm:flex-auto sm:flex-col-reverse">
          {isNotFirstStep && (
            <button
              onClick={handlePrint}
              className={`${
                printLoading ? 'cursor-not-allowed' : 'cursor-pointer'
              } flex items-center`}
              disabled={printLoading}
            >
              <img src={donwloadIcon} alt="" />
              <div className="font-medium text-primary ml-[8px] whitespace-nowrap">
                {t('download')} PDF
              </div>
            </button>
          )}

          <div className="flex items-center justify-center gap-2">
            <span 
              className={cn(
                'whitespace-nowrap',
                { 'text-disabledBorder': isNotFirstStep },
              )}
            
            >
              {t('easyVersion')}
            </span>
            <SwitchComponent
              checked={isExpertMode}
              onChange={setIsExpertMode}
              disabled={isNotFirstStep}
            />
            <span className={cn(
              'whitespace-nowrap',
              { 'text-disabledBorder': isNotFirstStep },
            )}>
              {t('advancedMode')}
            </span>
             
          </div>

          <div className="flex gap-2">
            {showBack && (
              <Button
                onClick={onBack}
                disabled={backDisabled}
                className="flex-auto p-0 w-[100px] h-[42px]"
                btnType="secondary"
              >
                {t('back')}
              </Button>
            )}
            <div className='w-[100px] sm:w-full'>
              {showNext && (
                <Button
                  onClick={runHandler}
                  disabled={disabled}
                  className="flex-auto p-0 w-[100px] h-[42px]"
                >
                  {t('next')}
                </Button>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export const Warning = () => {
  const { t } = useTranslation();

  return (
    <div className="px-[100px] py-[32px]">
      <div className="text-black font-bold md:text-lg mt-6 mb-8">
        {t('warning')}
      </div>
      <div>{t('warningText')}</div>
      <div className="text-black font-bold md:text-lg mt-6 mb-8">
        {t('disclaimer')}
      </div>
      <div>{t('disclaimerText')}</div>
    </div>
  );
};
